import React, { useState, useMemo, useCallback } from 'react';
import {
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useTheme as useMuiTheme,
  alpha,
  CircularProgress,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { ReportGenerationService } from '../../services/ReportGenerationService';
import { useAnalysisReportStore } from '../../stores/analysisReportStore';
import { AnalysisConfirmDialog } from '../AnalysisConfirmDialog';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';

// Import report generators
import { generateReports, createChangeOrderReports } from './reports';
import { useExtensionData } from '../closeout/quoteHelper';

// ContractBudgetReport is now managed by parent component via callback

// Import icons
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

// Helper function to create streaming reports
export const createStreamingReports = (isEstimateContext = true): Report[] => {
  const documentType = isEstimateContext ? 'Estimate' : 'Change Order';

  return [
    {
      id: 'estimate-report',
      name: `${documentType} Report`,
      description: `Generate a comprehensive ${documentType.toLowerCase()} report with AI analysis`,
      icon: <AssessmentIcon />,
      category: 'ai',
      generateStreaming: async options => {
        await ReportGenerationService.generateEstimateReport(options);
      },
    },
    {
      id: 'item-analysis',
      name: 'Extended Cost Analysis',
      description: 'Analyze extended cost items with detailed breakdown',
      icon: <AnalyticsIcon />,
      category: 'ai',
      generateStreaming: async options => {
        await ReportGenerationService.generateItemAnalysisReport(options);
      },
    },
  ];
};

// Enhanced report interface with universal support
export interface Report {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category?: 'extension' | 'contract' | 'estimate' | 'changeOrder' | 'ai';
  generate?: (reportOption?: string) => Promise<void>;
  generateForEditor?: (options?: { reportOption?: string }) => Promise<string>;
  generateStreaming?: (options: {
    projectId: string;
    estimateId: string;
    estimateName?: string;
    onChunk: (chunk: string) => void;
    onComplete?: (fullContent: string) => void;
    onError?: (error: Error) => void;
  }) => Promise<void>;
  hasOptions?: boolean;
  options?: {
    id: string;
    name: string;
    description: string;
    isDefault: boolean;
  }[];
  disabled?: boolean;
  disabledReason?: string;
  guidanceText?: string;
}

// Enhanced interface for complete independence
interface ReportsDialogProps {
  open: boolean;
  onClose: () => void;
  // Legacy support - if reports are passed, use them; otherwise generate all available
  reports?: Report[];
  onOpenEditor?: (title: string, content: string) => void;
  onOpenStreamingEditor?: (title: string, reportId: string) => void;
  // Optional context overrides - if missing, user can provide via interface
  projectId?: string;
  estimateId?: string;
  estimateName?: string;
  // New handlers for specialized reports
  onOpenChangeOrderReport?: () => void;
  // Complete independence - direct report opening
  directReportAccess?: boolean;
  // Portable data source props - allows use outside extension context
  extensionData?: any;
  refetchExtensionData?: () => Promise<any>;
  isExtensionDataLoading?: boolean;
  hasEstimateData?: boolean;
  hasChangeOrderData?: boolean;
  estimateError?: any;
  changeOrderError?: any;
}

export const ReportsDialog: React.FC<ReportsDialogProps> = ({
  open,
  onClose,
  reports: passedReports,
  onOpenEditor,
  onOpenStreamingEditor,
  projectId: propProjectId,
  estimateId: propEstimateId,
  estimateName: propEstimateName,
  onOpenChangeOrderReport,
  directReportAccess = true,
  // Portable data source props
  extensionData: propExtensionData,
  refetchExtensionData: propRefetchExtensionData,
  isExtensionDataLoading: propIsExtensionDataLoading,
  hasEstimateData: propHasEstimateData,
  hasChangeOrderData: propHasChangeOrderData,
  estimateError: propEstimateError,
  changeOrderError: propChangeOrderError,
}) => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingReportName, setGeneratingReportName] = useState('');
  const [selectedReportOption, setSelectedReportOption] = useState<string>('');
  const [showAnalysisConfirm, setShowAnalysisConfirm] = useState(false);
  const [cooldownTimer, setCooldownTimer] = useState<number>(0);

  // Remove internal ContractBudgetReport state - use parent callback instead

  const analysisReportStore = useAnalysisReportStore();

  // Get current context from global state
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;

  // Use props or global state for IDs
  const projectId = propProjectId || selectedProject?.id;
  const estimateId = propEstimateId || selectedEstimate?.estimateid;

  // Get extension data - use props if provided, otherwise fall back to hook
  const hookExtensionData = useExtensionData();
  const extensionData = propExtensionData ?? hookExtensionData.extensionData;
  const refetchExtensionData = propRefetchExtensionData ?? hookExtensionData.refetchExtensionData;
  const isExtensionDataLoading = propIsExtensionDataLoading ?? hookExtensionData.isLoading;
  const hasEstimateData = propHasEstimateData ?? hookExtensionData.hasEstimateData;
  const hasChangeOrderData = propHasChangeOrderData ?? hookExtensionData.hasChangeOrderData;
  const estimateError = propEstimateError ?? hookExtensionData.estimateError;
  const changeOrderError = propChangeOrderError ?? hookExtensionData.changeOrderError;

  // Determine current context and available data
  const currentContext = useMemo(() => {
    if (selectedChangeOrder) return 'changeOrder';
    if (selectedEstimate) return 'estimate';
    return 'closeout';
  }, [selectedChangeOrder, selectedEstimate]);

  const availableData = useMemo(() => {
    const data = {
      hasProject: !!selectedProject,
      hasEstimate: !!selectedEstimate,
      hasChangeOrder: !!selectedChangeOrder,
      hasExtensionData: !!extensionData?.extendestimatesList?.length,
      isExtensionDataLoading,
      hasCloseoutContext:
        currentContext === 'closeout' ||
        currentContext === 'estimate' ||
        currentContext === 'changeOrder',
    };

    return data;
  }, [
    selectedProject,
    selectedEstimate,
    selectedChangeOrder,
    extensionData,
    isExtensionDataLoading,
    currentContext,
  ]);

  // Generate all available reports with context awareness
  const allAvailableReports = useMemo(() => {
    // If reports are passed (legacy mode), use them
    if (passedReports && passedReports.length > 0) {
      return passedReports.map(report => ({
        ...report,
        category: report.category || 'extension',
        disabled: false,
      }));
    }

    // Generate universal report list
    const reports: Report[] = [];

    // Extension reports (CSV, Quoted Items) - need extension data
    if (availableData.hasExtensionData) {
      const extensionReports = generateReports(extensionData?.extendestimatesList || []);
      reports.push(
        ...extensionReports.map(report => ({
          ...report,
          category: 'extension' as const,
          disabled: false,
        }))
      );
    } else {
      // Add disabled extension reports with guidance
      reports.push(
        {
          id: 'csv-export-disabled',
          name: 'CSV Export',
          description: 'Export extension data to CSV with aggregation options',
          icon: <AssessmentIcon />,
          category: 'extension',
          disabled: true,
          disabledReason: 'Extension data not available',
          guidanceText:
            'Open a project with an estimate or change order, then navigate to the Closeout view to access extension data.',
        },
        {
          id: 'quoted-items-disabled',
          name: 'Quoted Items Report',
          description: 'Generate bill of materials organized by quote groups',
          icon: <AssignmentIcon />,
          category: 'extension',
          disabled: true,
          disabledReason: 'Extension data not available',
          guidanceText:
            'Open a project with an estimate or change order, then navigate to the Closeout view to access extension data.',
        }
      );
    }

    // AI-powered streaming reports - need project and estimate/change order
    if (availableData.hasProject && (availableData.hasEstimate || availableData.hasChangeOrder)) {
      const streamingReports = createStreamingReports(!!selectedEstimate);
      reports.push(
        ...streamingReports.map(report => ({
          ...report,
          disabled: false,
        }))
      );
    } else {
      // Add disabled AI reports with guidance
      const docType = availableData.hasEstimate
        ? 'Estimate'
        : availableData.hasChangeOrder
          ? 'Change Order'
          : 'Document';
      reports.push(
        {
          id: 'estimate-report-disabled',
          name: `${docType} Report`,
          description: `Generate a comprehensive ${docType.toLowerCase()} report with AI analysis`,
          icon: <AssessmentIcon />,
          category: 'ai',
          disabled: true,
          disabledReason: !availableData.hasProject
            ? 'No project selected'
            : 'No estimate or change order selected',
          guidanceText: !availableData.hasProject
            ? 'Select a project first, then open an estimate or change order to generate AI reports.'
            : 'Open an estimate or change order to generate AI-powered reports.',
        },
        {
          id: 'item-analysis-disabled',
          name: 'Extended Cost Analysis',
          description: 'Analyze extended cost items with detailed breakdown',
          icon: <AnalyticsIcon />,
          category: 'ai',
          disabled: true,
          disabledReason: !availableData.hasProject
            ? 'No project selected'
            : 'No estimate or change order selected',
          guidanceText: !availableData.hasProject
            ? 'Select a project first, then open an estimate or change order to generate analysis reports.'
            : 'Open an estimate or change order to generate cost analysis.',
        }
      );
    }

    // Enhanced Change Order/Estimate Report - needs closeout context
    if (availableData.hasCloseoutContext) {
      const changeOrderReports = createChangeOrderReports(!!selectedEstimate);
      reports.push(
        ...changeOrderReports.map(report => ({
          ...report,
          category: selectedEstimate ? ('estimate' as const) : ('changeOrder' as const),
          disabled: false,
          generate: async () => {
            if (onOpenChangeOrderReport) {
              onOpenChangeOrderReport();
              // Don't call onClose() here - let the parent component handle it
            } else {
              // Fallback: dispatch event
              const event = new CustomEvent('openChangeOrderDialog', {
                detail: { reportType: 'enhanced' },
              });
              window.dispatchEvent(event);
              onClose();
            }
          },
        }))
      );
    } else {
      // Add disabled enhanced report with guidance
      reports.push({
        id: 'enhanced-report-disabled',
        name: 'Enhanced Estimate Report',
        description: 'Comprehensive report with detailed breakdowns of all cost categories',
        icon: <AssignmentIcon />,
        category: 'estimate',
        disabled: true,
        disabledReason: 'Requires closeout context',
        guidanceText:
          'Open an estimate or change order and navigate to the Closeout view to access enhanced reports.',
      });
    }

    return reports;
  }, [
    passedReports,
    availableData,
    extensionData,
    selectedEstimate,
    onOpenChangeOrderReport,
    onClose,
  ]);

  // Group reports by category
  const reportsByCategory = useMemo(() => {
    const grouped: Record<string, Report[]> = {};
    allAvailableReports.forEach(report => {
      const category = report.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(report);
    });
    return grouped;
  }, [allAvailableReports]);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'extension':
        return 'Extension Reports';
      case 'estimate':
        return 'Estimate Reports';
      case 'changeOrder':
        return 'Change Order Reports';
      case 'ai':
        return 'AI-Powered Reports';
      default:
        return 'Other Reports';
    }
  };

  const getCategoryDescription = (category: string) => {
    switch (category) {
      case 'extension':
        return 'Reports based on extended cost data';
      case 'estimate':
        return 'Estimate-specific detailed reports';
      case 'changeOrder':
        return 'Change order specific reports';
      case 'ai':
        return 'AI-generated analysis and insights';
      default:
        return '';
    }
  };

  const handleGenerateReport = useCallback(async () => {
    if (!selectedReport) return;

    const report = allAvailableReports.find(r => r.id === selectedReport);
    if (!report || report.disabled) return;

    // Special handling for Extended Cost Analysis (item-analysis) ONLY
    if (report.id === 'item-analysis' && estimateId) {
      const existingReport = analysisReportStore.getReport(estimateId);
      const isOnCooldown = analysisReportStore.isOnCooldown(estimateId);

      // Always update cooldown timer to ensure it's current
      const remainingTime = analysisReportStore.getCooldownTimeRemaining(estimateId);
      setCooldownTimer(remainingTime);

      if (existingReport && isOnCooldown) {
        // Open streaming editor with cached report
        if (onOpenStreamingEditor) {
          onOpenStreamingEditor(report.name, report.id);
        }
        onClose();
        return;
      }

      // If we have a cached report but no cooldown, still offer to show it or generate new
      if (existingReport && !isOnCooldown) {
        // Show confirmation dialog for new analysis when not on cooldown
        setShowAnalysisConfirm(true);
        return;
      }

      // No existing report, show confirmation for new analysis
      if (!existingReport) {
        setShowAnalysisConfirm(true);
        return;
      }
    }

    setIsGenerating(true);
    setGeneratingReportName(report.name);

    try {
      // Check if this is a streaming report that requires project and document data
      if (report.generateStreaming && projectId && estimateId) {
        // For non-analysis reports, open streaming editor immediately without cooldown checks
        if (onOpenStreamingEditor) {
          onOpenStreamingEditor(report.name, report.id);
          // Close the reports dialog
          onClose();
          return;
        } else {
          alert('Streaming editor not available. Please try again.');
          return;
        }
      } else if (report.generateForEditor) {
        // For extension reports, check if we can generate the report at all
        if (report.category === 'extension') {
          // If we have no data and no way to get it, show error immediately
          if (!hasEstimateData && !hasChangeOrderData && (estimateError || changeOrderError)) {
            let errorMessage = 'Cannot generate extension report. ';
            if (estimateError && changeOrderError) {
              errorMessage +=
                'Please ensure you have either an estimate or change order selected with extension data.';
            } else if (estimateError) {
              errorMessage += 'Please ensure you have an estimate selected with extension data.';
            } else if (changeOrderError) {
              errorMessage += 'Please ensure you have a change order selected with extension data.';
            }

            alert(errorMessage);
            return;
          }
        }

        // For extension reports, ensure data is loaded and refreshed
        let freshExtensionData = extensionData;
        if (report.category === 'extension') {
          // If data is loading, wait for it to complete
          if (isExtensionDataLoading) {
            // Wait for data to load (with timeout)
            let attempts = 0;
            const maxAttempts = 10; // 5 seconds max wait
            while (isExtensionDataLoading && attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 500));
              attempts++;
            }
          }

          // Refresh data to ensure we have the latest
          if (refetchExtensionData) {
            try {
              const result = await refetchExtensionData();
              freshExtensionData = result.data;
            } catch (refreshError) {
              console.warn('Failed to refresh extension data:', refreshError);
              // Continue with existing data
            }
          } else {
            freshExtensionData = extensionData;
          }

          // Check if we have data after refresh
          const hasFreshData = (freshExtensionData?.extendestimatesList?.length || 0) > 0;

          if (!hasFreshData) {
            console.error('No extension data available after refresh', {
              hasEstimateData,
              hasChangeOrderData,
              estimateError:
                estimateError instanceof Error ? estimateError.message : String(estimateError),
              changeOrderError:
                changeOrderError instanceof Error
                  ? changeOrderError.message
                  : String(changeOrderError),
              selectedEstimate: !!selectedEstimate,
              selectedChangeOrder: !!selectedChangeOrder,
            });

            let errorMessage = 'No extension data available. ';
            if (estimateError && changeOrderError) {
              errorMessage +=
                'Please ensure you have either an estimate or change order selected with extension data.';
            } else if (estimateError) {
              errorMessage += 'Please ensure you have an estimate selected with extension data.';
            } else if (changeOrderError) {
              errorMessage += 'Please ensure you have a change order selected with extension data.';
            } else {
              errorMessage +=
                'Please ensure you have an estimate with extension data and try again.';
            }

            alert(errorMessage);
            return;
          }
        }

        // Generate content first
        try {
          // For extension reports, regenerate the report with fresh data
          let reportToUse = report;
          if (report.category === 'extension') {
            // Use the fresh data we just fetched
            const dataToUse = freshExtensionData || extensionData;

            // Regenerate the reports with the fresh extension data
            const freshReports = generateReports(dataToUse?.extendestimatesList || []);

            const freshReport = freshReports.find(r => r.id === report.id);
            if (freshReport) {
              reportToUse = freshReport;
            } else {
              console.warn('Could not find fresh report, using original for:', report.id);
            }
          }

          // Pass the selected option if the report has options
          const options =
            reportToUse.hasOptions && selectedReportOption
              ? { reportOption: selectedReportOption }
              : undefined;

          if (!reportToUse.generateForEditor) {
            throw new Error('Report generation function not available');
          }

          const content = await reportToUse.generateForEditor(options);

          // Only open editor after content is generated
          if (onOpenEditor) {
            onOpenEditor(report.name, content);

            // Close the reports dialog
            onClose();
            return;
          } else {
            alert('Editor not available. Please try again.');
            return;
          }
        } catch (contentError) {
          console.error('Error generating report content:', contentError);
          alert(
            `Error generating report content: ${contentError instanceof Error ? contentError.message : 'Unknown error'}. Please try again.`
          );
          return;
        }
      } else if (report.generate) {
        // Direct generation (download) or special handler
        // Pass the selected option if the report has options
        const reportOption =
          report.hasOptions && selectedReportOption ? selectedReportOption : undefined;
        await report.generate(reportOption);
        onClose();
        return;
      } else {
        console.error('No generation method available for report:', report.id);
        alert('No generation method available for this report. Please try again.');
        return;
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert(
        `Error generating report: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`
      );
    } finally {
      setIsGenerating(false);
      setGeneratingReportName('');
    }
  }, [
    selectedReport,
    allAvailableReports,
    selectedReportOption,
    projectId,
    estimateId,
    analysisReportStore,
    onOpenStreamingEditor,
    onOpenEditor,
    onClose,
    isExtensionDataLoading,
    refetchExtensionData,
    extensionData,
    hasEstimateData,
    hasChangeOrderData,
    estimateError,
    changeOrderError,
    selectedEstimate,
    selectedChangeOrder,
  ]);

  const handleDialogClose = () => {
    // Don't allow closing while generating
    if (isGenerating) return;

    // Reset all states
    setSelectedReport(null);
    setSelectedReportOption('');
    setIsGenerating(false);
    setGeneratingReportName('');
    onClose();
  };

  // Reset states when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedReport(null);
      setSelectedReportOption('');
      setIsGenerating(false);
      setGeneratingReportName('');

      // Only refresh cooldown timer for item analysis reports when dialog opens
      if (estimateId) {
        const remaining = analysisReportStore.getCooldownTimeRemaining(estimateId);
        setCooldownTimer(remaining);
      }
    }
  }, [open, estimateId, analysisReportStore]);

  // Set default option when a report with options is selected
  React.useEffect(() => {
    if (selectedReport) {
      const report = allAvailableReports.find(r => r.id === selectedReport);
      if (report?.hasOptions && report.options) {
        const defaultOption = report.options.find(opt => opt.isDefault);
        if (defaultOption) {
          setSelectedReportOption(defaultOption.id);
        }
      } else {
        setSelectedReportOption('');
      }
    }
  }, [selectedReport, allAvailableReports]);

  // Check initial cooldown ONLY for item analysis reports
  React.useEffect(() => {
    if (estimateId) {
      const remaining = analysisReportStore.getCooldownTimeRemaining(estimateId);
      if (remaining > 0) {
        setCooldownTimer(remaining);
      }
    }
  }, [estimateId, analysisReportStore]);

  // Cooldown timer effect - ONLY for item analysis reports
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (estimateId && cooldownTimer > 0) {
      interval = setInterval(() => {
        const remaining = analysisReportStore.getCooldownTimeRemaining(estimateId);
        setCooldownTimer(remaining);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [estimateId, cooldownTimer, analysisReportStore]);

  const handleAnalysisConfirm = () => {
    setShowAnalysisConfirm(false);

    if (!selectedReport || !estimateId) return;

    const report = allAvailableReports.find(r => r.id === selectedReport);
    if (!report) return;

    setIsGenerating(true);
    setGeneratingReportName(report.name);

    // Proceed with streaming report generation
    if (report.generateStreaming && projectId && estimateId) {
      if (onOpenStreamingEditor) {
        onOpenStreamingEditor(report.name, report.id);
      }
      onClose();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Reports Dialog - Box instead of Dialog */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
        onClick={e => {
          // Close on backdrop click
          if (e.target === e.currentTarget && !isGenerating) {
            handleDialogClose();
          }
        }}
      >
        <Box
          sx={{
            backgroundColor: muiTheme.palette.background.paper,
            borderRadius: 1,
            width: '100%',
            maxWidth: 'md',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: muiTheme.shadows[24],
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              pb: 0,
              position: 'relative',
              borderBottom: `1px solid ${muiTheme.palette.divider}`,
            }}
          >
            <Typography variant="h6">
              {isGenerating ? `Generating ${generatingReportName}...` : 'Universal Reports'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Context: {currentContext} • Available: {Object.keys(reportsByCategory).length}{' '}
              categories
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleDialogClose}
              disabled={isGenerating}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: isGenerating
                  ? muiTheme.palette.action.disabled
                  : muiTheme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
            {isGenerating ? (
              // Loading state
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  gap: 2,
                }}
              >
                <CircularProgress size={48} thickness={4} />
                <Typography variant="h6" color="primary">
                  Generating Report
                </Typography>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  Please wait while we generate your {generatingReportName.toLowerCase()}...
                  <br />
                  This may take a few moments.
                </Typography>
              </Box>
            ) : (
              // Report selection
              <>
                {allAvailableReports.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No Reports Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No reports can be generated. Try selecting a project, contract, or estimate
                      first.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Select a report to generate. Available reports depend on your current context
                      and data.
                    </Typography>

                    {/* Show loading indicator when extension data is loading */}
                    {isExtensionDataLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                          p: 2,
                          bgcolor: alpha(muiTheme.palette.info.main, 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha(muiTheme.palette.info.main, 0.2)}`,
                        }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="info.main">
                          Loading extension data...
                        </Typography>
                      </Box>
                    )}

                    {Object.entries(reportsByCategory).map(([category, reports]) => (
                      <Box key={category} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {getCategoryLabel(category)}
                          </Typography>
                          <Chip
                            label={`${reports.filter(r => !r.disabled).length}/${reports.length}`}
                            size="small"
                            sx={{ ml: 1 }}
                            color={reports.some(r => !r.disabled) ? 'primary' : 'default'}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'block' }}
                        >
                          {getCategoryDescription(category)}
                        </Typography>

                        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                          {reports.map(report => {
                            const isAnalysisReport = report.id === 'item-analysis';
                            const isOnCooldown =
                              isAnalysisReport &&
                              estimateId &&
                              analysisReportStore.isOnCooldown(estimateId);

                            return (
                              <ListItem key={report.id} disablePadding>
                                <ListItemButton
                                  selected={selectedReport === report.id}
                                  onClick={() => !report.disabled && setSelectedReport(report.id)}
                                  disabled={report.disabled}
                                  sx={{
                                    borderRadius: 1,
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor:
                                      selectedReport === report.id
                                        ? muiTheme.palette.primary.main
                                        : alpha(muiTheme.palette.divider, 0.1),
                                    opacity: report.disabled ? 0.6 : 1,
                                    '&.Mui-selected': {
                                      backgroundColor: alpha(
                                        muiTheme.palette.primary.main,
                                        darkMode ? 0.2 : 0.1
                                      ),
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 40 }}>{report.icon}</ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: selectedReport === report.id ? 600 : 400,
                                          }}
                                        >
                                          {report.name}
                                        </Typography>
                                        {report.category === 'ai' && !report.disabled && (
                                          <Chip label="AI" size="small" color="primary" />
                                        )}
                                        {isOnCooldown && (
                                          <Chip label="Cached" size="small" color="warning" />
                                        )}
                                        {report.disabled && (
                                          <Chip label="Unavailable" size="small" color="default" />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography variant="body2" color="text.secondary">
                                          {report.description}
                                        </Typography>
                                        {report.disabled && report.guidanceText && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'flex-start',
                                              mt: 1,
                                              gap: 0.5,
                                            }}
                                          >
                                            <InfoIcon
                                              sx={{
                                                fontSize: 14,
                                                color: 'text.secondary',
                                                mt: 0.2,
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontStyle: 'italic' }}
                                            >
                                              {report.guidanceText}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    ))}

                    {/* Report Options Dropdown */}
                    {selectedReport &&
                      (() => {
                        const report = allAvailableReports.find(r => r.id === selectedReport);
                        return report?.hasOptions && report.options ? (
                          <Box sx={{ mt: 3 }}>
                            <FormControl fullWidth>
                              <InputLabel id="report-option-label">Report Options</InputLabel>
                              <Select
                                labelId="report-option-label"
                                value={selectedReportOption}
                                label="Report Options"
                                onChange={e => setSelectedReportOption(e.target.value)}
                              >
                                {report.options.map(option => (
                                  <MenuItem key={option.id} value={option.id}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {option.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                      >
                                        {option.description}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : null;
                      })()}
                  </>
                )}
              </>
            )}
          </Box>

          {/* Footer */}
          {!isGenerating && (
            <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handleDialogClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleGenerateReport}
                variant="contained"
                color="primary"
                disabled={(() => {
                  if (!selectedReport) return true;
                  const report = allAvailableReports.find(r => r.id === selectedReport);
                  if (!report || report.disabled) return true;
                  return false;
                })()}
              >
                {(() => {
                  if (!selectedReport) return 'Generate Report';
                  const report = allAvailableReports.find(r => r.id === selectedReport);
                  if (!report) return 'Generate Report';

                  // Only show cooldown-specific button text for item-analysis report
                  const isAnalysisReport = report.id === 'item-analysis';
                  const isOnCooldown =
                    isAnalysisReport && estimateId && analysisReportStore.isOnCooldown(estimateId);

                  if (isOnCooldown) {
                    return 'View Cached Report';
                  }
                  return 'Generate Report';
                })()}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ContractBudgetReport is now managed by parent component via callback */}

      {/* Analysis Confirmation Dialog */}
      <AnalysisConfirmDialog
        open={showAnalysisConfirm}
        onClose={() => setShowAnalysisConfirm(false)}
        onConfirm={handleAnalysisConfirm}
      />
    </>
  );
};
